import { Component, computed } from "vue";
import { useI18n } from "vue-i18n";
import {
    buttonField,
    createBlock,
    descriptiveImageField,
    headingField,
    htmlField,
    textField,
} from "./blockBuilder";

import { CtaBaseProps } from "@/views/blocks/cta/cta.interfaces";
import screenshotImage from "@/views/blocks/cta/shared/images/page-top-left.png";
import type { BlockDef } from "./block.interfaces";
import { useDefaultColors } from "./useDefaultColors";
import { PanelWithAppScreenshot } from "@/views/blocks/cta/cta";

export function useCtas() {
    const { t } = useI18n();
    const { colorDefaults } = useDefaultColors();

    function createBaseCtaBlock({
        component,
        typeId,
        name,
        extendBlock,
    }: {
        component: Component;
        typeId: string;
        name: string;
        extendBlock?: (block: ReturnType<typeof createBlock>) => ReturnType<typeof createBlock>;
    }): BlockDef {
        let block = createBlock<CtaBaseProps>({
            typeId,
            type: "cta",
            name: t(name),
            component,
        })
            .withField("tag", textField("Text", "Advanced Technology", "Text"))
            .withField("title", headingField("Title", "Transform Business with Easytools", "h2"))
            .withField(
                "description",
                htmlField(
                    "Description",
                    "Our platform simplifies complex workflows and transforms your business operations. Experience seamless integration today."
                )
            )
            .withField(
                "primaryButton",
                buttonField("Primary button", {
                    label: "Get started",
                    href: "https://cart.easy.tools/register",
                })
            )
            .withField(
                "secondaryButton",
                buttonField("Secondary button", {
                    label: "Learn more",
                    href: "https://www.easy.tools/changelog/easytools-quick-start",
                })
            )
            .withField(
                "image",
                descriptiveImageField("Image", { src: screenshotImage, alt: "Cta image" })
            )
            .withStyleFields([
                colorDefaults.value.background,
                colorDefaults.value.text,
                colorDefaults.value.brand,
                colorDefaults.value.border,
            ]);

        if (extendBlock) {
            block = extendBlock(block);
        }

        return block.build();
    }

    const panelWithAppScreenshotBlock = computed<BlockDef>(() =>
        createBaseCtaBlock({
            typeId: "PanelWithAppScreenshot",
            name: "Call to action with app screenshot",
            component: PanelWithAppScreenshot,
        })
    );

    return {
        blocks: computed(() => [panelWithAppScreenshotBlock.value]),
    };
}
